import { useCallback, useEffect, useState } from "react";
import { Trophy, Users } from "lucide-react";
import { toast } from "react-toastify";
import { getLeaderboardByExam } from "../../services/TestService";

const formatTime = (seconds) => {
  if (!seconds && seconds !== 0) return "-";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const ExamLeaderboardList = ({ examId, showHeader = true, initialLimit = 20 }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalParticipants, setTotalParticipants] = useState(0);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const fetchRows = useCallback(
    async (offset) => {
      if (!examId) return;
      try {
        setLoading(true);
        const res = await getLeaderboardByExam(examId, { limit: initialLimit, offset });
        const newRows = res?.data || [];
        const total = res?.metadata?.totalParticipants ?? 0;

        if (offset === 0) {
          setRows(newRows);
        } else {
          setRows((prev) => [...prev, ...newRows]);
        }

        setTotalParticipants(total);
        setCurrentOffset(offset);
        setHasMore(offset + initialLimit < total);
      } catch (error) {
        const message = error?.response?.data?.message || "Không thể tải bảng xếp hạng";
        toast.error(message);
      } finally {
        setLoading(false);
      }
    },
    [examId, initialLimit]
  );

  useEffect(() => {
    fetchRows(0);
  }, [fetchRows]);

  const handleLoadMore = () => {
    fetchRows(currentOffset + initialLimit);
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      {showHeader && (
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-red-600" />
            Bảng xếp hạng đề thi
          </h3>
          <div className="text-sm text-gray-500 flex items-center gap-1">
            <Users className="w-4 h-4" />
            {totalParticipants} người tham gia
          </div>
        </div>
      )}

      {loading && rows.length === 0 ? (
        <div className="p-10 text-center text-gray-500">Đang tải...</div>
      ) : rows.length === 0 ? (
        <div className="p-10 text-center text-gray-500">
          Chưa có dữ liệu bảng xếp hạng cho đề thi này
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-gray-500 border-b border-gray-100 bg-gray-50">
                  <th className="px-6 py-3 w-20">Hạng</th>
                  <th className="px-6 py-3">Học viên</th>
                  <th className="px-6 py-3 text-center w-32">Điểm</th>
                  <th className="px-6 py-3 text-center w-40">Thời gian</th>
                  <th className="px-6 py-3 text-right w-48">Nộp lúc</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((item) => (
                  <tr
                    key={`${item.userId}-${item.rank}`}
                    className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 font-bold text-red-600">#{item.rank}</td>
                    <td className="px-6 py-4">
                      <div className="font-semibold text-gray-900">{item.fullName}</div>
                      <div className="text-xs text-gray-400">{item.email || "-"}</div>
                    </td>
                    <td className="px-6 py-4 text-center font-bold text-gray-900">
                      {item.score}
                    </td>
                    <td className="px-6 py-4 text-center text-gray-700">
                      {formatTime(item.examCompledTime)}
                    </td>
                    <td className="px-6 py-4 text-right text-gray-600 text-sm">
                      {item.submittedAt
                        ? new Date(item.submittedAt).toLocaleString("vi-VN")
                        : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {hasMore && (
            <div className="px-6 py-4 border-t border-gray-100 text-center">
              <button
                onClick={handleLoadMore}
                disabled={loading}
                className="px-6 py-2 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50 text-sm font-semibold transition-colors"
              >
                {loading ? "Đang tải..." : "Xem thêm"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ExamLeaderboardList;
